// src/components/PainPoints.jsx

import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';

// Importation des images
import incompleteFile from '../../assets/babyCryingIcon.png';
import consentIcon from '../../assets/confusedIcon.png';
import statusIcon from '../../assets/stressLogo.png';
import questionImg from '../../assets/question.png';
import moneyImg from '../../assets/money.png';
import questionCloudImg from '../../assets/questionCloud.png';
import botLogoImg from '../../assets/botLogo.png';
import timelineImg from '../../assets/timeline.png';
import bills from '../../assets/bills.png';
import dashLogo from '../../assets/dash3.png';
import timelineIcon from '../../assets/timelineIcon4.png';
import folderIcon from "../../assets/folder.png";
import checklistphoto from "../../assets/checklistphoto.png";
import calculatricephoto from "../../assets/calculatricephoto.png";
import guidedivorcephoto from "../../assets/guidedivorcephoto.png";
import interfacefacilephoto from "../../assets/interfacefacilephoto.png";
import calculettelogo from "../../assets/calculettelogo.png";

const PainPoints = () => {
  const { t } = useTranslation();
  
  // Récupérer les erreurs et solutions depuis les traductions
  const errors = t('painpoints.errors', { returnObjects: true }) || [];
  const solutionsItems = t('painpoints.solutions.items', { returnObjects: true }) || [];
  const interfaceFeatures = t('painpoints.solutions.interface.features', { returnObjects: true }) || [];
  
  return (
    <>
      {/* SECTION 1 : Erreurs du dossier familial */}
      <section
        id="painpoints"
        className="w-full bg-gray-50 text-gray-700 py-12 md:py-16 px-4"
      >
        <div className="max-w-7xl mx-auto">
          {/* Illustration d'entête */}
          <div className="flex justify-center mb-8">
            <img
              src={statusIcon}
              alt="Stress ou confusion"
              className="w-24 h-24 md:w-32 md:h-32 object-contain"
            />
          </div>

          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold leading-tight text-gray-800 mb-4">
              {t('painpoints.title')}
            </h2>
          </div>

          {/* Cartes d'erreurs */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch mb-16">
            {errors.map((error, index) => (
              <div key={index} className="bg-white rounded-xl shadow-md p-6 text-center hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="flex justify-center">
                  <img
                    src={index === 0 ? incompleteFile : index === 1 ? consentIcon : bills}
                    alt={error.title}
                    className="w-16 h-16 mb-4 object-contain"
                  />
                </div>
                <h4 className="text-xl font-bold mb-3 text-primary">
                  {error.title}
                </h4>
                <p className="text-gray-600 text-sm">
                  {error.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 2 : Solutions myPlum+ */}
      <section className="w-full bg-blue-50 py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-extrabold mb-4 tracking-tight">
              {t('painpoints.solutions.title')}
            </h2>
            <h2 className="text-3xl md:text-4xl font-extrabold mb-8 tracking-tight">
              <span className="bg-primary text-white px-4 py-2 rounded-lg shadow-lg">
                {t('painpoints.solutions.subtitle')}
              </span>
            </h2>
          </div>

          {/* Solutions Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch mb-20">
            {/* Solution 1: Dossier Complet */}
            <div className="flex flex-col items-center bg-white rounded-xl shadow-md p-6 min-h-[400px] md:min-h-[500px] hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <div className="w-16 h-16 md:w-20 md:h-20 mb-6 flex items-center justify-center">
                <img
                  src={folderIcon}
                  alt="Checklist documents"
                  className="w-full h-full object-contain"
                />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-800 text-center">
                {solutionsItems[0]?.title || "Dossier complet"}
              </h3>
              <p className="text-gray-600 text-center mb-8 text-lg leading-relaxed">
                {solutionsItems[0]?.description || "Notre checklist intelligente vous aide à vérifier les documents requis pour votre dossier"}
              </p>
              <div className="mt-auto w-full flex justify-center">
                <img
                  src={checklistphoto}
                  alt="Système de vérification des documents"
                  className="w-full h-[250px] object-cover rounded-lg shadow-sm"
                />
              </div>
            </div>

            {/* Solution 2: Procédure Claire */}
            <div className="flex flex-col items-center bg-white rounded-xl shadow-md p-6 min-h-[400px] md:min-h-[500px] hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <div className="w-16 h-16 md:w-20 md:h-20 mb-6 flex items-center justify-center">
                <img
                  src={timelineImg}
                  alt="Timeline interactive"
                  className="w-full h-full object-contain"
                />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-800 text-center">
                {solutionsItems[1]?.title || "Procédure Pas à Pas"}
              </h3>
              <p className="text-gray-600 text-center mb-8 text-lg leading-relaxed">
                {solutionsItems[1]?.description || "Un guide étape par étape avec notre timeline interactive adaptable à votre situation"}
              </p>
              <div className="mt-auto w-full flex justify-center">
                <img
                  src={timelineIcon}
                  alt="Progression interactive"
                  className="w-full h-[250px] object-cover rounded-lg shadow-sm"
                />
              </div>
            </div>

            {/* Solution 3: Maîtrise des Coûts */}
            <div className="flex flex-col items-center bg-white rounded-xl shadow-md p-6 min-h-[400px] md:min-h-[500px] hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <div className="w-16 h-16 md:w-20 md:h-20 mb-6 flex items-center justify-center">
                <img
                  src={moneyImg}
                  alt="Outil d'estimation de coûts"
                  className="w-full h-full object-contain"
                />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-800 text-center">
                {solutionsItems[2]?.title || "Coûts anticipés"}
              </h3>
              <p className="text-gray-600 text-center mb-8 text-lg leading-relaxed">
                {solutionsItems[2]?.description || "Estimez et planifiez vos frais potentiels avec nos outils d'estimation interactifs"}
              </p>
              <div className="mt-auto w-full flex justify-center">
                <img
                  src={calculettelogo}
                  alt="Estimation des coûts"
                  className="w-full h-[250px] object-cover rounded-lg shadow-sm"
                />
              </div>
            </div>
          </div>

          {/* Dashboard Section */}
          <div className="mt-16">
            <h2 className="text-3xl md:text-4xl font-extrabold text-center mb-8 md:mb-12 tracking-tight px-4">
              {t('painpoints.solutions.interface.title')}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 items-stretch">
              {/* Feature 1 */}
              <div className="flex flex-col items-center bg-white rounded-xl shadow-md p-4 md:p-6 h-[500px] md:h-[600px] hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="w-12 h-12 md:w-16 md:h-16 mb-3 md:mb-4 flex items-center justify-center">
                  <img
                    src={questionImg}
                    alt="Guide"
                    className="w-full h-full object-contain"
                  />
                </div>
                <h3 className="text-xl md:text-2xl font-bold mb-2 md:mb-4 text-gray-800 text-center">
                  {interfaceFeatures[0]?.title || "Guides interactifs"}
                </h3>
                <p className="text-gray-600 text-center mb-4 md:mb-6 text-base md:text-lg leading-relaxed">
                  {interfaceFeatures[0]?.description || "Accédez à des tutoriels et informations utiles"}
                </p>
                <div className="flex-1 w-full flex justify-center items-center overflow-hidden px-2 md:px-4">
                  <div className="relative w-full h-full">
                    <img
                      src={guidedivorcephoto}
                      alt="Guide interface"
                      className="w-full h-full object-contain rounded-lg shadow-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Feature 2 */}
              <div className="flex flex-col items-center bg-white rounded-xl shadow-md p-4 md:p-6 h-[500px] md:h-[600px] hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="w-12 h-12 md:w-16 md:h-16 mb-3 md:mb-4 flex items-center justify-center">
                  <img
                    src={botLogoImg}
                    alt="Assistant"
                    className="w-full h-full object-contain"
                  />
                </div>
                <h3 className="text-xl md:text-2xl font-bold mb-2 md:mb-4 text-gray-800 text-center">
                  {interfaceFeatures[1]?.title || "Assistant intelligent"}
                </h3>
                <p className="text-gray-600 text-center mb-4 md:mb-6 text-base md:text-lg leading-relaxed">
                  {interfaceFeatures[1]?.description || "Un guide intelligent à chaque étape"}
                </p>
                <div className="flex-1 w-full flex justify-center items-center overflow-hidden px-2 md:px-4">
                  <div className="relative w-full h-full">
                    <img
                      src={interfacefacilephoto}
                      alt="Assistant interface"
                      className="w-full h-full object-contain rounded-lg shadow-sm"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default PainPoints;
